import { CommonModule } from '@angular/common';
import { A11yModule } from '@angular/cdk/a11y';
import { AfterViewChecked, Component, ElementRef, HostListener, OnDestroy, OnInit, computed, effect, input, output, signal, viewChild } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { DateRange } from '@angular/material/datepicker';
import { Subscription } from 'rxjs';
import { SoCalendarUI } from '../so-calendar/so-calendar.component';
import { SoButtonGradientUI } from '../so-button-gradient/so-button-gradient.component';
import { DateRangePosition } from '../so-date-picker/so-date-picker.interfaces';
import { buildPresets, PRESETS } from './presets';
import { SoDateRangePreset } from './so-date-range-picker.interfaces';

@Component({
  selector: 'so-date-range-picker-ui',
  standalone: true,
  imports: [CommonModule, SoCalendarUI, A11yModule, SoButtonGradientUI],
  templateUrl: './so-date-range-picker.component.html',
  styleUrls: ['./so-date-range-picker.component.scss'],
})
export class SoDateRangePickerUI implements OnInit, OnDestroy, AfterViewChecked {
  private overlayRef = viewChild<ElementRef<HTMLElement>>('overlayPanel');
  private needsRepositioning = false;
  private triggerElement: HTMLElement | null = null;
  selectLabel = input<string>('Fecha de ejecución');
  selectDisplay = input<'vertical' | 'horizontal'>('horizontal');
  placeholder = input<string>('Seleccionar');
  selectedText = input<string | null>(null);
  isFilter = input<boolean>(false);
  selectedValue = input<string | number | null>(null);
  selectDisabled = input<boolean>(false);
  showComplementaryCalendar = input<boolean>(false);
  triggerOffset = input<number>(8);
  selectHeight = input<number>(36);
  overlayTitle = input<string>('Seleccionar rango de fechas');
  formGroup = input<FormGroup | null>(null);
  startControlName = input<string>('startDate');
  endControlName = input<string>('endDate');
  maxWidth = input<string>();

  opened = output<void>();
  closed = output<DateRange<Date> | null>();

  private touched = false;
  private subscriptions: Subscription[] = [];
  private updatingControls = false;

  private lastExternalSelectedText = signal<string | null>(null);
  private selectedTextState = signal<string | null>(null);
  effectiveSelectedText = computed(() => this.selectedTextState() ?? this.selectedText());

  isOpen = signal(false);
  position = signal<DateRangePosition | null>(null);
  presets = signal<SoDateRangePreset[]>(PRESETS.map(item => ({ ...item })));
  selectedDateRange = signal<DateRange<Date> | null>(null);

  isInvalid = computed(() => {
    if (!this.hasFormControls) return false;
    const start = this.startControl;
    const end = this.endControl;
    const touched = !!(start?.touched || end?.touched);
    return touched && !!(start?.invalid || end?.invalid);
  });

  isValid = computed(() => {
    if (!this.hasFormControls) return false;
    const start = this.startControl;
    const end = this.endControl;
    const touched = !!(start?.touched || end?.touched);
    return touched && !!(start?.valid && end?.valid);
  });

  constructor() {
    effect(() => {
      const external = this.selectedText();
      if (external === this.lastExternalSelectedText()) return;
      this.lastExternalSelectedText.set(external);
      this.selectedTextState.set(external);
    }, {  });

    effect(() => {
      this.formGroup();
      this.startControlName();
      this.endControlName();
      queueMicrotask(() => this.setupFormControlSubscriptions());
    }, {  });
  }

  ngOnInit(): void {
    this.setDateRange(this.getSelectedRange());
    this.setupFormControlSubscriptions();
  }

  ngOnDestroy(): void {
    this.clearSubscriptions();
  }

  ngAfterViewChecked(): void {
    if (this.needsRepositioning && this.isOpen()) {
      this.needsRepositioning = false;
      this.adjustOverlayPosition();
    }
  }

  private adjustOverlayPosition(): void {
    const overlayEl = this.overlayRef()?.nativeElement;
    if (!overlayEl) return;

    const rect = overlayEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const currentPosition = this.position();
    if (!currentPosition) return;

    let newTop = currentPosition.top;
    let newLeft = currentPosition.left;

    // Ajustar si el overlay se sale por abajo del viewport
    if (rect.bottom > viewportHeight) {
      const overflow = rect.bottom - viewportHeight;
      newTop = Math.max(8, currentPosition.top - overflow - 8);
    }

    // Ajustar si el overlay se sale por la derecha del viewport
    if (rect.right > viewportWidth) {
      const overflow = rect.right - viewportWidth;
      newLeft = Math.max(8, currentPosition.left - overflow - 8);
    }

    // Solo actualizar si cambió la posición
    if (newTop !== currentPosition.top || newLeft !== currentPosition.left) {
      this.position.set({
        ...currentPosition,
        top: newTop,
        left: newLeft,
      });
    }
  }

  public onClose(selectDateRange = this.selectedDateRange()): void {
    if (!this.isOpen()) return;

    if (!selectDateRange) {
      this.selectedTextState.set(null);
      this.selectedDateRange.set(null);
      this.setAllInactive();
      this.selectPreset(0);
      this.updateFormControls(null);
      this.closed.emit(selectDateRange);
    }

    this.isOpen.set(false);
    queueMicrotask(() => this.triggerElement?.focus());

    if (this.touched) {
      this.closed.emit(selectDateRange);
    }
  }

  public onTriggerPointerDown(event: MouseEvent): void {
    if (this.selectDisabled()) return;
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement | null;
    this.openOverlay(target);
    this.touched = false;
  }

  public onTriggerKeyDown(event: KeyboardEvent): void {
    if (this.selectDisabled()) return;
    const triggerKeys = ['Enter', ' ', 'Spacebar'];
    if (!triggerKeys.includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement | null;
    this.openOverlay(target);
  }

  private openOverlay(target: HTMLElement | null): void {
    if (!target) return;
    this.triggerElement = target;
    const rect = target.getBoundingClientRect();
    this.position.set({
      top: rect.bottom + this.triggerOffset(),
      left: rect.left,
      width: rect.width,
    });
    this.refreshPresets();
    this.needsRepositioning = true;
    this.isOpen.set(true);
    this.opened.emit();
    queueMicrotask(() => this.overlayRef()?.nativeElement.querySelector<HTMLElement>('button, [tabindex]:not([tabindex="-1"])')?.focus());
  }

  private refreshPresets(): void {
    const previousActiveIndex = this.presets().findIndex(preset => preset.active);
    const refreshedPresets = buildPresets();
    refreshedPresets.forEach((preset, index) => { preset.active = index === previousActiveIndex; });
    this.presets.set(refreshedPresets);
  }

  public selectPreset(index: number): void {
    const current = this.presets();
    const isAlreadySelected = current[index]?.active;
    
    this.setAllInactive();
    
    if (isAlreadySelected) {
      this.selectedDateRange.set(null);
      return;
    }

    const updatedPresets = current.map((preset, i) => 
      i === index ? { ...preset, active: true } : { ...preset, active: false }
    );
    this.presets.set(updatedPresets);

    const selectedPreset = updatedPresets[index];
    if (selectedPreset) {
      const newRange = new DateRange<Date>(
        new Date(selectedPreset.range.start),
        new Date(selectedPreset.range.end)
      );
      this.selectedDateRange.set(newRange);
    }
  }

  private getSelectedRange(): DateRange<Date> | null {
    const selectedPreset = this.presets().find(item => item.active);
    if (!selectedPreset) return null;
    return new DateRange<Date>(selectedPreset.range.start, selectedPreset.range.end);
  }

  public setDateRange(dateRange: { start: Date; end: Date } | DateRange<Date> | null): void {
    if (!dateRange) return;
    const start = (dateRange as any).start ?? null;
    const end = (dateRange as any).end ?? null;
    this.selectedDateRange.set(new DateRange<Date>(start, end));
  }

  public updateSelectedText(): void {
    this.touched = true;
    const date = this.selectedDateRange();
    if (!date) return;
    const formatted = this.formatRange(date);
    this.selectedTextState.set(formatted);
    this.updateFormControls(date);
    this.onClose();
  }

  private setAllInactive(): void {
    const current = this.presets();
    this.presets.set(current.map(item => ({ ...item, active: false })));
  }

  private setupFormControlSubscriptions(): void {
    this.clearSubscriptions();

    if (!this.hasFormControls) return;

    this.updateDateRangeFromControls();

    const startControl = this.startControl!;
    const endControl = this.endControl!;

    this.subscriptions.push(
      startControl.valueChanges.subscribe(() => this.updateDateRangeFromControls()),
      endControl.valueChanges.subscribe(() => this.updateDateRangeFromControls())
    );
  }

  private updateDateRangeFromControls(): void {
    if (!this.hasFormControls || this.updatingControls) return;

    const startValue = this.toDate(this.startControl?.value ?? null);
    const endValue = this.toDate(this.endControl?.value ?? null);

    if (!startValue && !endValue) {
      this.selectedDateRange.set(null);
      this.selectedTextState.set(null);
      return;
    }

    const range = new DateRange<Date>(startValue ?? null, endValue ?? null);
    this.setAllInactive();
    this.selectedDateRange.set(range);
    const formatted = this.formatRange(range);
    this.selectedTextState.set(formatted);
  }

  private updateFormControls(range: DateRange<Date> | null): void {
    if (!this.hasFormControls) return;

    const startControl = this.startControl!;
    const endControl = this.endControl!;

    const startValue = range?.start ?? null;
    const endValue = range?.end ?? null;

    this.updatingControls = true;
    startControl.setValue(startValue, { emitEvent: false });
    endControl.setValue(endValue, { emitEvent: false });
    this.updatingControls = false;
  }

  private get hasFormControls(): boolean {
    if (!this.formGroup()) return false;
    const start = this.startControl;
    const end = this.endControl;
    return !!start && !!end;
  }

  private get startControl(): AbstractControl<Date | null> | null {
    const fg = this.formGroup();
    const name = this.startControlName();
    if (!fg || !name) return null;
    return fg.get(name) as AbstractControl<Date | null> | null;
  }

  private get endControl(): AbstractControl<Date | null> | null {
    const fg = this.formGroup();
    const name = this.endControlName();
    if (!fg || !name) return null;
    return fg.get(name) as AbstractControl<Date | null> | null;
  }

  private toDate(value: unknown): Date | null {
    if (value instanceof Date && !isNaN(value.getTime())) return value;
    if (typeof value === 'string' || typeof value === 'number') {
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  }

  private formatRange(date: DateRange<Date>): string {
    const formatter = new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    const startDateString = date.start ? formatter.format(date.start) : '';
    const endDateString = date.end ? formatter.format(date.end) : '';
    return `${startDateString} - ${endDateString}`;
  }

  private clearSubscriptions(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];
  }

  @HostListener('window:keydown.escape')
  onEscape(): void {
    this.onClose(null);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.onClose(null);
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.onClose(null);
  }
}
